import React, { useState, useEffect, useRef } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftSpinner,
} from "@craft-design-system/core-components-react";
import {
	Plus,
	Trash2,
	Pencil,
	RefreshCw as Refresh,
	Search,
	X,
} from "lucide-react";
import { apiClient } from "../../services/api";

const CognitoUserManagementModal = ({ open, onClose, currentUser }) => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [tabValue, setTabValue] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	// Multiple users form state
	const [userForms, setUserForms] = useState([
		{
			id: 1,
			username: "",
			email: "",
			user_role: "user",
			user_access: "read",
		},
	]);
	// Edit User State
	const [editingUser, setEditingUser] = useState(null);
	const [editFormData, setEditFormData] = useState({
		user_access: "",
	});
	const addFormsEndRef = useRef(null);

	// Fetch all users
	const fetchUsers = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiClient.get("/cognito/users", {
				params: { current_user: currentUser },
			});

			if (response.data.success) {
				setUsers(response.data.users);
			}
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || "Failed to fetch users";
			setError(errorMessage);
			console.error("Error fetching users:", {
				message: errorMessage,
				status: err.response?.status,
				url: "/cognito/users",
				error: err,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			fetchUsers();
		}
	}, [open]);

	// Handle tab change
	const handleTabChange = (newValue) => {
		setTabValue(newValue);
		setError(null);
		setSuccess(null);
	};

	// Add new user form
	const handleAddUserForm = () => {
		setUserForms([
			...userForms,
			{
				id: Date.now(),
				username: "",
				email: "",
				user_role: "user",
				user_access: "read",
			},
		]);
	};

	// Scroll to the newest add-user form when one is appended
	useEffect(() => {
		if (tabValue === 1 && addFormsEndRef.current) {
			addFormsEndRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [userForms.length, tabValue]);

	// Remove user form
	const handleRemoveUserForm = (id) => {
		if (userForms.length === 1) return; // Keep at least one form
		setUserForms(userForms.filter((form) => form.id !== id));
	};

	// Update user form field
	const handleUserFormChange = (id, field, value) => {
		setUserForms(
			userForms.map((form) => {
				if (form.id === id) {
					const updatedForm = { ...form, [field]: value };

					// Auto-fill username from email
					if (field === "email" && value) {
						const emailParts = value.split("@");
						if (emailParts.length > 0) {
							updatedForm.username = emailParts[0];
						}
					}

					return updatedForm;
				}
				return form;
			}),
		);
	};

	// Handle create users
	const handleCreateUsers = async () => {
		// Validate all forms
		const validForms = userForms.filter((form) => form.username && form.email);

		if (validForms.length === 0) {
			setError("Please fill in at least one user with username and email");
			return;
		}

		setLoading(true);
		setError(null);
		setSuccess(null);

		try {
			let successCount = 0;
			let failCount = 0;
			const failedUsers = [];

			for (const user of validForms) {
				try {
					const response = await apiClient.post(
						"/cognito/users/create",
						{
							username: user.username,
							email: user.email,
							user_role: user.user_role,
							user_access: user.user_access,
						},
						{ params: { current_user: currentUser } },
					);

					if (response.data.success) {
						successCount++;
					}
				} catch (err) {
					failCount++;
					failedUsers.push(`${user.username} (${user.email})`);
				}
			}

			if (successCount > 0) {
				setSuccess(
					`Successfully created ${successCount} user(s)${failCount > 0 ? `. Failed: ${failCount}` : ""}`,
				);
				// Reset forms
				setUserForms([
					{
						id: Date.now(),
						username: "",
						email: "",
						user_role: "user",
						user_access: "read",
					},
				]);
				fetchUsers();
			}

			if (failedUsers.length > 0) {
				setError(`Failed to create: ${failedUsers.join(", ")}`);
			}
		} catch (err) {
			setError("Failed to create users");
			console.error("Create users error:", err);
		} finally {
			setLoading(false);
		}
	};

	// Handle edit user
	const handleEditUser = (user) => {
		setEditingUser(user);
		setEditFormData({
			user_access: user.user_access === "N/A" ? "read" : user.user_access,
		});
	};

	// Handle update user access
	const handleUpdateAccess = async () => {
		if (!editingUser) return;

		setLoading(true);
		setError(null);
		setSuccess(null);

		try {
			const response = await apiClient.put(
				"/cognito/users/update-access",
				{
					email: editingUser.email,
					user_role: "user", // Always set to 'user' since admins are hardcoded
					user_access: editFormData.user_access,
				},
				{
					params: { current_user: currentUser },
				},
			);

			if (response.data.success) {
				setSuccess(`User access updated successfully!`);
				setEditingUser(null);
				fetchUsers();
			}
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || "Failed to update user access";
			setError(errorMessage);
			console.error("Error updating user:", {
				message: errorMessage,
				status: err.response?.status,
				url: "/cognito/users/update-access",
				error: err,
			});
		} finally {
			setLoading(false);
		}
	};

	// Handle delete user
	const handleDeleteUser = async (email) => {
		if (!window.confirm(`Are you sure you want to delete user ${email}?`)) {
			return;
		}

		setLoading(true);
		setError(null);
		setSuccess(null);

		try {
			const response = await apiClient.delete("/cognito/users/delete", {
				params: {
					email: email,
					current_user: currentUser,
				},
			});

			if (response.data.success) {
				setSuccess(`User ${email} deleted successfully!`);
				fetchUsers();
			}
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || "Failed to delete user";
			setError(errorMessage);
			console.error("Error deleting user:", {
				message: errorMessage,
				status: err.response?.status,
				url: "/cognito/users/delete",
				error: err,
			});
		} finally {
			setLoading(false);
		}
	};

	// Filter users based on search term (email and status only)
	const filteredUsers = users.filter((user) => {
		if (!searchTerm) return true;
		const search = searchTerm.toLowerCase();
		return (
			user.email?.toLowerCase().includes(search) ||
			user.cognito_status?.toLowerCase().includes(search)
		);
	});

	// Chip color palettes
	const chipPalette = {
		success: { bg: "#e8f5e9", border: "#b7dfb9", color: "#2e7d32" },
		warning: { bg: "#fff4e6", border: "#ffd699", color: "#b26a00" },
		error: { bg: "#fde8e8", border: "#f5c6c6", color: "#cc0000" },
		primary: { bg: "#f0f8ff", border: "#b3d9ff", color: "#1565c0" },
		default: { bg: "#f2f2f2", border: "#ddd", color: "#555" },
	};

	const chipStyle = (variant) => {
		const p = chipPalette[variant] || chipPalette.default;
		return {
			display: "inline-block",
			backgroundColor: p.bg,
			border: `1px solid ${p.border}`,
			color: p.color,
			borderRadius: "12px",
			padding: "1px 10px",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			whiteSpace: "nowrap",
		};
	};

	const getStatusVariant = (status) => {
		switch (status) {
			case "CONFIRMED":
				return "success";
			case "FORCE_CHANGE_PASSWORD":
				return "warning";
			case "DELETED":
				return "error";
			default:
				return "default";
		}
	};

	const s = {
		// Constrained shell: body scrolls internally so footer stays visible.
		shell: {
			width: "900px",
			maxWidth: "100%",
			display: "flex",
			flexDirection: "column",
			maxHeight: "60vh",
		},
		alert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#fde8e8",
			border: "1px solid #f5c6c6",
			color: "#cc0000",
			whiteSpace: "pre-line",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			flexShrink: 0,
		},
		successAlert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#e8f5e9",
			border: "1px solid #b7dfb9",
			color: "#2e7d32",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			flexShrink: 0,
		},
		tabBar: {
			display: "flex",
			gap: "4px",
			borderBottom: "1px solid #e5e7eb",
			marginBottom: "12px",
			flexShrink: 0,
		},
		tab: (active) => ({
			background: "none",
			border: "none",
			borderBottom: active ? "2px solid #cc0000" : "2px solid transparent",
			cursor: "pointer",
			padding: "8px 14px",
			fontSize: "13px",
			color: active ? "#1e2832" : "#666",
			fontFamily: active
				? "MAN EUROPE_Bold, Arial, sans-serif"
				: "MAN EUROPE_Regular, Arial, sans-serif",
		}),
		toolbar: {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: "10px",
			gap: "12px",
			flexShrink: 0,
		},
		searchWrap: {
			display: "flex",
			alignItems: "center",
			gap: "6px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			padding: "5px 10px",
			flex: 1,
			maxWidth: "320px",
		},
		searchInput: {
			border: "none",
			outline: "none",
			fontSize: "13px",
			width: "100%",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		count: {
			fontSize: "12px",
			color: "#666",
			whiteSpace: "nowrap",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		ghostBtn: {
			background: "none",
			border: "1px solid #ddd",
			borderRadius: "4px",
			padding: "5px 12px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "5px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			color: "#555",
		},
		addBtn: {
			background: "none",
			border: "1px solid #4caf50",
			borderRadius: "4px",
			padding: "5px 12px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "5px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#2e7d32",
		},
		tableWrap: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			overflow: "auto",
			flex: 1,
			minHeight: 0,
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			fontSize: "13px",
		},
		th: {
			textAlign: "left",
			padding: "10px 12px",
			backgroundColor: "#f8f9fa",
			borderBottom: "1px solid #e5e7eb",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			fontSize: "12px",
			color: "#374151",
			position: "sticky",
			top: 0,
			zIndex: 1,
			whiteSpace: "nowrap",
		},
		thCenter: { textAlign: "center" },
		td: {
			padding: "8px 12px",
			borderBottom: "1px solid #f0f0f0",
			color: "#333",
			verticalAlign: "middle",
		},
		tdCenter: { textAlign: "center" },
		iconBtn: {
			background: "none",
			border: "none",
			cursor: "pointer",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "4px",
			borderRadius: "4px",
		},
		input: {
			width: "100%",
			boxSizing: "border-box",
			padding: "6px 8px",
			border: "1px solid #ccc",
			borderRadius: "4px",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		select: {
			width: "100%",
			boxSizing: "border-box",
			padding: "6px 8px",
			border: "1px solid #ccc",
			borderRadius: "4px",
			fontSize: "13px",
			backgroundColor: "white",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		label: {
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#555",
			marginBottom: "4px",
			display: "block",
		},
		formCard: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			padding: "14px",
			marginBottom: "12px",
			backgroundColor: "#f8f9fa",
		},
		formCardHead: {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: "10px",
		},
		formGrid: {
			display: "flex",
			gap: "12px",
			marginBottom: "10px",
			flexWrap: "wrap",
		},
		formCol: { flex: 1, minWidth: "220px" },
		empty: {
			textAlign: "center",
			padding: "40px 20px",
			color: "#999",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		loadingWrap: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			padding: "40px",
			gap: "12px",
		},
		formsScroll: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
		},
	};

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<CraftModalHeader
				header="User Management"
				content={`Manage Cognito users and access levels. Total users: ${users.length}.`}
			/>
			<CraftModalBody>
				<div style={s.shell}>
					{/* Tabs */}
					<div style={s.tabBar}>
						<button
							type="button"
							style={s.tab(tabValue === 0)}
							onClick={() => handleTabChange(0)}
						>
							All Users
						</button>
						<button
							type="button"
							style={s.tab(tabValue === 1)}
							onClick={() => handleTabChange(1)}
						>
							Add Users
						</button>
					</div>

					{/* Alerts */}
					{error && <div style={s.alert}>⚠️ {error}</div>}
					{success && <div style={s.successAlert}>✓ {success}</div>}

					{/* Tab 0: All Users */}
					{tabValue === 0 && (
						<>
							<div style={s.toolbar}>
								<div style={s.searchWrap}>
									<Search size={14} color="#888" />
									<input
										style={s.searchInput}
										placeholder="Search by Email or Status…"
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
									/>
								</div>
								<span style={s.count}>
									Showing {filteredUsers.length} of {users.length} users
								</span>
								<button
									type="button"
									onClick={fetchUsers}
									disabled={loading}
									style={s.ghostBtn}
								>
									<Refresh size={13} /> Refresh
								</button>
							</div>

							{loading ? (
								<div style={s.loadingWrap}>
									<CraftSpinner size="m" />
									<span style={{ color: "#666" }}>Loading users…</span>
								</div>
							) : (
								<div style={s.tableWrap}>
									<table style={s.table}>
										<thead>
											<tr>
												<th style={s.th}>Username</th>
												<th style={s.th}>Email</th>
												<th style={s.th}>Status</th>
												<th style={s.th}>Access</th>
												<th style={{ ...s.th, ...s.thCenter }}>Enabled</th>
												<th style={{ ...s.th, ...s.thCenter }}>In DB</th>
												<th style={{ ...s.th, ...s.thCenter }}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{filteredUsers.map((user) => (
												<tr key={user.email}>
													<td style={s.td}>{user.username}</td>
													<td style={s.td}>{user.email}</td>
													<td style={s.td}>
														<span
															style={chipStyle(
																getStatusVariant(user.cognito_status),
															)}
														>
															{user.cognito_status}
														</span>
													</td>
													<td style={s.td}>
														{user.user_access && user.user_access !== "N/A" ? (
															<span
																style={chipStyle(
																	user.user_access === "write"
																		? "primary"
																		: "default",
																)}
															>
																{user.user_access}
															</span>
														) : (
															<span
																style={{ color: "#999", fontStyle: "italic" }}
															>
																-
															</span>
														)}
													</td>
													<td style={{ ...s.td, ...s.tdCenter }}>
														<span
															style={chipStyle(
																user.enabled ? "success" : "default",
															)}
														>
															{user.enabled ? "Yes" : "No"}
														</span>
													</td>
													<td style={{ ...s.td, ...s.tdCenter }}>
														<span
															style={chipStyle(
																user.in_database ? "success" : "warning",
															)}
														>
															{user.in_database ? "Yes" : "No"}
														</span>
													</td>
													<td style={{ ...s.td, ...s.tdCenter }}>
														<button
															type="button"
															title="Edit Access"
															style={{ ...s.iconBtn, color: "#1565c0" }}
															onClick={() => handleEditUser(user)}
														>
															<Pencil size={16} />
														</button>
														<button
															type="button"
															title="Delete User"
															style={{ ...s.iconBtn, color: "#cc0000" }}
															onClick={() => handleDeleteUser(user.email)}
														>
															<Trash2 size={16} />
														</button>
													</td>
												</tr>
											))}
											{filteredUsers.length === 0 && (
												<tr>
													<td colSpan={7} style={s.empty}>
														No users found
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							)}
						</>
					)}

					{/* Tab 1: Add Users */}
					{tabValue === 1 && (
						<>
							<div style={s.toolbar}>
								<span style={s.count}>
									{userForms.length} user form
									{userForms.length !== 1 ? "s" : ""}
								</span>
								<button
									type="button"
									style={s.addBtn}
									onClick={handleAddUserForm}
								>
									<Plus size={13} /> Add Another User
								</button>
							</div>

							<div style={s.formsScroll}>
								{userForms.map((form, index) => (
									<div key={form.id} style={s.formCard}>
										<div style={s.formCardHead}>
											<span
												style={{
													fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
													fontSize: "13px",
													color: "#1e2832",
												}}
											>
												User {index + 1}
											</span>
											{userForms.length > 1 && (
												<button
													type="button"
													title="Remove"
													style={{ ...s.iconBtn, color: "#cc0000" }}
													onClick={() => handleRemoveUserForm(form.id)}
												>
													<X size={16} />
												</button>
											)}
										</div>
										<div style={s.formGrid}>
											<div style={s.formCol}>
												<label style={s.label}>Email</label>
												<input
													style={s.input}
													type="email"
													value={form.email}
													onChange={(e) =>
														handleUserFormChange(
															form.id,
															"email",
															e.target.value.toLowerCase(),
														)
													}
													placeholder="e.g., john.doe@man.eu"
												/>
											</div>
											<div style={s.formCol}>
												<label style={s.label}>Username (Auto-generated)</label>
												<input
													style={{
														...s.input,
														backgroundColor: "#f0f0f0",
														color: "#666",
													}}
													value={form.username}
													placeholder="Auto-filled from email"
													readOnly
												/>
											</div>
										</div>
										<div style={s.formGrid}>
											<div style={s.formCol}>
												<label style={s.label}>Access Level</label>
												<select
													style={s.select}
													value={form.user_access}
													onChange={(e) =>
														handleUserFormChange(
															form.id,
															"user_access",
															e.target.value,
														)
													}
												>
													<option value="read">Read Only</option>
													<option value="write">Read & Write</option>
												</select>
											</div>
										</div>
									</div>
								))}
								<div ref={addFormsEndRef} />
							</div>

							<CraftButton
								variant="primary"
								size="m"
								onClick={handleCreateUsers}
								disabled={loading}
							>
								{loading
									? `Creating ${userForms.length} User(s)…`
									: `Create ${userForms.length} User(s)`}
							</CraftButton>
						</>
					)}
				</div>
			</CraftModalBody>
			<CraftModalFooter>
				<CraftButton variant="primary" size="m" onClick={onClose}>
					Close
				</CraftButton>
			</CraftModalFooter>

			{/* Edit User Access — nested modal */}
			<CraftModal
				show={editingUser !== null}
				allowClickOut={true}
				onCraftClose={() => setEditingUser(null)}
			>
				<CraftModalHeader
					header="Edit User Access"
					content={editingUser ? editingUser.email : ""}
				/>
				<CraftModalBody>
					<div style={{ width: "360px", maxWidth: "100%" }}>
						<label style={s.label}>Access Level</label>
						<select
							style={s.select}
							value={editFormData.user_access}
							onChange={(e) =>
								setEditFormData({
									...editFormData,
									user_access: e.target.value,
								})
							}
						>
							<option value="read">Read Only</option>
							<option value="write">Read & Write</option>
						</select>
					</div>
				</CraftModalBody>
				<CraftModalFooter>
					<CraftButton
						variant="plain"
						size="m"
						onClick={() => setEditingUser(null)}
					>
						Cancel
					</CraftButton>
					<CraftButton
						variant="primary"
						size="m"
						onClick={handleUpdateAccess}
						disabled={loading}
					>
						Update
					</CraftButton>
				</CraftModalFooter>
			</CraftModal>
		</CraftModal>
	);
};

export default CognitoUserManagementModal;
